import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, must, type RegisteredIssuer } from "../api/client";
import { useWallet } from "../wallet/context";
import { ACCEPT, prepareArtifact, type Prepared } from "../artifact/prepare";
import { NameBasisBadge, basisLabel } from "../components/NameBasisBadge";

const PASTE = "__paste";
/** How an issuer reads in the list: "<name> · <badge>" (a light issuer by its domain). */
export function issuerOption(i: RegisteredIssuer): string {
  if (i.kind === "light") return `${i.domain ?? i.name} · light issuer, domain + second person`;
  const badge = basisLabel(i.name_basis, i.domain);
  return badge ? `${i.name} · ${badge}` : i.name;
}

/** /claim: describe something you did, name the issuer, attach the artifact (hashed here; the file never leaves the browser). */
export function ClaimNewPage() {
  const { wallet, identity } = useWallet();
  const nav = useNavigate();
  const [types, setTypes] = useState<any[]>([]);
  const [issuers, setIssuers] = useState<RegisteredIssuer[]>([]);
  const [claimType, setClaimType] = useState("");
  const [issuer, setIssuer] = useState("");          // the DID the claim names ("" for none)
  const [choice, setChoice] = useState("");          // the select's value: a listed DID, "", or PASTE
  const [pasted, setPasted] = useState("");
  const [resolved, setResolved] = useState<RegisteredIssuer | null>(null);
  const [lookupErr, setLookupErr] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [tags, setTags] = useState("");
  const [artifact, setArtifact] = useState<Prepared | null>(null);
  const [artifactErr, setArtifactErr] = useState("");
  const [preparing, setPreparing] = useState(false);
  const pick = async (f?: File) => {
    setArtifact(null); setArtifactErr("");
    if (!f) return;
    setPreparing(true);
    try { setArtifact(await prepareArtifact(f)); } catch (e) { setArtifactErr((e as Error).message); } finally { setPreparing(false); }
  };
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.GET("/registry/claim-types").then((r) => { setTypes(r.data?.claim_types ?? []); setClaimType(r.data?.claim_types?.[0]?.id ?? ""); });
    // Full and light issuers from the public directory. An issuer missing from it can still be named by its DID.
    api.GET("/registry/issuers").then((r) => setIssuers(r.data?.issuers ?? []));
  }, []);

  if (!identity) return <div><h1>Make a claim</h1><p className="lead">You need a key in this browser first.</p><a className="btn primary" href="/me?return=/claim">Set up your keys</a></div>;

  const full = issuers.filter((i) => i.kind === "full").sort((a, b) => a.name.localeCompare(b.name));
  const light = issuers.filter((i) => i.kind === "light").sort((a, b) => (a.domain ?? a.name).localeCompare(b.domain ?? b.name));
  const choose = (v: string) => { setChoice(v); setResolved(null); setLookupErr(""); setIssuer(v === PASTE ? "" : v); };
  // Resolve a pasted DID through the issuer lookup (registered issuers, listed or not).
  const lookup = async () => {
    const did = pasted.trim();
    setResolved(null); setLookupErr(""); setIssuer("");
    if (!/^did:[a-z0-9]+:\S+$/.test(did)) return setLookupErr("That is not a DID.");
    const r = await api.GET("/issuers/{issuer_did}/claims", { params: { path: { issuer_did: did } } });
    if (r.data?.issuer) { setResolved(r.data.issuer); setIssuer(did); }
    else setLookupErr("No registered issuer has this DID. Check it, or name no issuer.");
  };
  const pasting = choice === PASTE;

  const schemaProps = Object.keys(types.find((t) => t.id === claimType)?.schema?.properties ?? {});
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const entity: Record<string, unknown> = { holder_did: identity.did, claim_type: claimType, description: description.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean) };
      const f = Object.fromEntries(Object.entries(fields).filter(([, v]) => v.trim()));
      if (Object.keys(f).length) entity.fields = f;
      if (artifact) { entity.artifact_hash = artifact.hash; entity.artifact_mime = artifact.mime; }
      if (issuer) entity.issuer_did = issuer;
      const out = await must(api.POST("/claims", { body: { jws: await wallet.signEnvelope("claim", entity) } }));
      // The prepared file stays on this device: each vouch link gets its own encrypted copy of it.
      if (artifact) await wallet.saveArtifact(out.claim_id, { bytes: artifact.bytes, mime: artifact.mime, hash: artifact.hash });
      nav(`/c/${out.claim_id}`);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div>
      <h1>Make a claim</h1>
      <p className="lead">Say what you did, in your own words. Others vouch for it; an issuer can anchor it.</p>
      {err && <p className="error" role="alert">{err}</p>}
      <label htmlFor="ct">Claim type</label>
      <select id="ct" value={claimType} onChange={(e) => setClaimType(e.target.value)} data-testid="claim-type">
        {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      <label htmlFor="desc">Description</label>
      <textarea id="desc" maxLength={280} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="description" />
      <div className="hint">{280 - description.length} characters left</div>
      {schemaProps.map((p) => (<div key={p}><label htmlFor={`f-${p}`}>{p.replace(/_/g, " ")}</label>
        <input id={`f-${p}`} value={fields[p] ?? ""} onChange={(e) => setFields({ ...fields, [p]: e.target.value })} data-testid={`field-${p}`} /></div>))}
      <label htmlFor="iss">Issuer this claim names</label>
      <select id="iss" value={choice} onChange={(e) => choose(e.target.value)} data-testid="issuer">
        <option value="">None</option>
        {full.length > 0 && <optgroup label="Issuers">{full.map((i) => <option key={i.did} value={i.did}>{issuerOption(i)}</option>)}</optgroup>}
        {light.length > 0 && <optgroup label="Organisations (light issuers)">{light.map((i) => <option key={i.did} value={i.did}>{issuerOption(i)}</option>)}</optgroup>}
        <option value={PASTE}>Issuer not listed? Paste its DID</option>
      </select>
      {pasting && (
        <div className="card" style={{ marginTop: 8 }}>
          <label htmlFor="iss-did">The issuer's DID</label>
          <input id="iss-did" value={pasted} onChange={(e) => { setPasted(e.target.value); setResolved(null); setLookupErr(""); setIssuer(""); }}
                 placeholder="did:web:…" spellCheck={false} autoComplete="off" data-testid="issuer-did" />
          <div className="row" style={{ marginTop: 8 }}><button disabled={!pasted.trim()} onClick={lookup} data-testid="issuer-lookup">Look it up</button></div>
          {resolved && <p data-testid="issuer-resolved">Names <b>{resolved.kind === "light" ? resolved.domain ?? resolved.name : resolved.name}</b>{" "}
            <NameBasisBadge basis={resolved.name_basis} domain={resolved.domain} /></p>}
          {lookupErr && <p className="error" role="alert" data-testid="issuer-error">{lookupErr}</p>}
          <p className="hint">Not being in the public directory never stops you naming your issuer.</p>
        </div>
      )}
      <label htmlFor="tags">Tags</label>
      <input id="tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma separated" data-testid="tags" />
      <label htmlFor="art">A photo or a PDF (optional)</label>
      <div className="row">
        <label className="btn" htmlFor="art-cam">Take a photo</label>
        <input id="art-cam" type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.target.files?.[0])} />
        <label className="btn" htmlFor="art">Choose a file</label>
        <input id="art" type="file" accept={ACCEPT} hidden onChange={(e) => pick(e.target.files?.[0])} data-testid="artifact-file" />
      </div>
      {preparing && <div className="hint">Preparing it on this device…</div>}
      {artifactErr && <p className="error" role="alert">{artifactErr}</p>}
      {artifact && (
        <div className="hint" data-testid="artifact-ready">
          Ready: {artifact.mime === "application/pdf" ? "PDF" : `JPEG ${artifact.width} × ${artifact.height}`}, {Math.round(artifact.bytes.length / 1024)} KB
          {artifact.downscaled ? " (made smaller here)" : ""} · {artifact.hash.slice(0, 19)}…
        </div>
      )}
      <div className="hint">Only its SHA-256 goes into the claim. The file is encrypted on this device for each person you ask to vouch, with a key that travels only in their link; the rail stores only the encrypted copy and never sees the key.</div>
      <div className="row" style={{ marginTop: 18 }}><button className="primary" disabled={busy || preparing || !description.trim() || !claimType || (pasting && !issuer)} onClick={submit} data-testid="submit-claim">Sign and publish the claim</button></div>
    </div>
  );
}
